import { Link, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useQuery as useReactQuery } from 'react-query';
import {
  getRelatedNotes,
  RelatedNotesResponse,
} from '@dispatch/shared/services';
import { RelatedTasksDrawer } from './RelatedTasksDrawer';

export interface RelatedTasksLinkProps {
  claimNumber?: string;
  excludeTaskId?: string;
}

/**
 * RelatedTasksLink - click-to-load link that opens the related-tasks drawer for
 * a claim. Fetching is click-only (enabled: false). After the fetch:
 *   - results > 0  → "Related notes (N)", opens the drawer
 *   - results == 0 → disabled "No related notes" (no drawer); the link stays
 *                    visible so the empty answer is explicit, not a silent vanish
 *   - fetch error  → stays enabled "Related notes" with a retry tooltip, so an
 *                    outage isn't mistaken for "no notes"
 */
export const RelatedTasksLink: React.FC<RelatedTasksLinkProps> = ({
  claimNumber,
  excludeTaskId,
}) => {
  const [open, setOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const { data, isFetching, isError, refetch } =
    useReactQuery<RelatedNotesResponse>(
      ['related-notes', claimNumber, excludeTaskId],
      () => getRelatedNotes(claimNumber as string, excludeTaskId),
      {
        enabled: false,
      }
    );

  const handleClick = async () => {
    if (!claimNumber) return;
    const { data: refreshed } = await refetch();
    setHasFetched(true);
    // Only open the drawer when there's something to show. An empty result
    // flips the link to a disabled "No related notes" (handled below) rather
    // than opening an empty drawer.
    if ((refreshed?.results?.length ?? 0) > 0) {
      setOpen(true);
    }
  };

  const resultCount = data?.results?.length ?? 0;

  // Without a claim number there is nothing to fetch and handleClick is a
  // no-op, so don't render a dead link.
  if (!claimNumber) {
    return null;
  }

  // A successful fetch that returned nothing (no related tasks/notes): keep the
  // link visible but disable it and relabel to "No related notes", so the user
  // gets an explicit answer instead of the link silently vanishing. Errors are
  // excluded (isError) so an outage isn't mistaken for "no notes" — those keep
  // the enabled "Related notes" label + retry tooltip.
  const noResults = hasFetched && !isError && !isFetching && resultCount === 0;

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={handleClick}
        disabled={noResults}
        data-testid="related-tasks-link"
        title={
          isError ? "Couldn't load related notes — click to retry" : undefined
        }
        sx={{
          cursor: noResults ? 'default' : 'pointer',
          ...(noResults && {
            color: 'text.disabled',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
          }),
        }}
      >
        {isFetching ? (
          <CircularProgress size={14} />
        ) : noResults ? (
          'No related notes'
        ) : hasFetched && !isError ? (
          `Related notes (${resultCount})`
        ) : (
          'Related notes'
        )}
      </Link>
      {open && data && resultCount > 0 && (
        <RelatedTasksDrawer
          data={data}
          claimNumber={claimNumber}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};
